import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ChevronRight, KeyRound, ListChecks } from "lucide-react";
import { AgencyLoginButton } from "@/components/AgencyLogins";
import { HEAD, PdLoading } from "@/components/PdPill";

const DATE_LABEL = { today: "Today", yesterday: "Yesterday", last7: "Last 7 days", all: "All time" };
const sectionLbl = "text-[11px] uppercase tracking-[0.2em] text-[#6B6B61] font-semibold";

export function DmHome({ data, user, date }) {
  const navigate = useNavigate();
  if (!data) return <PdLoading />;
  const logins = data.logins || [];
  const saved = logins.filter((l) => l.username && l.password).length;
  const today = new Date().toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "long", day: "numeric", month: "short" });
  return (
    <div className="space-y-7 animate-in fade-in duration-300" data-testid="dm-home">
      <section>
        <p className={sectionLbl}>{today}</p>
        <h2 className={`${HEAD} text-3xl font-semibold text-[#1A1A1A] mt-1 leading-tight`} data-testid="dm-greeting">Namaste, {user?.display_name}</h2>
        <p className="text-sm text-[#6B6B61] mt-1">{logins.length} agencies · Pending: {DATE_LABEL[date] || date}</p>
      </section>
      <Tiles t={data.totals} logins={logins.length} saved={saved} go={(p) => navigate(`/delivery/${p}`)} />
      <AgencyList logins={logins} agencies={data.agencies} go={(gid) => navigate(`/delivery/pending?agency=${gid}`)} />
    </div>
  );
}

function Tiles({ t, logins, saved, go }) {
  const tiles = [
    ["pending", "Pending", t.pending, `${t.urgent} urgent`, ListChecks, "", true],
    ["complaint", "Complaint", t.complaint, "DAC ✗", AlertTriangle, "text-[#D9534F]"],
    ["done", "Aaj Done", t.done_today, "aaj mark kiye", CheckCircle2, "text-[#385C4B]"],
    ["logins", "Logins", logins, `${saved} saved`, KeyRound, "text-[#8A6046]"],
  ];
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="dm-tiles">
      {tiles.map(([k, label, value, sub, Icon, tone, primary], i) => (
        <button key={k} onClick={() => go(k)} data-testid={`dm-tile-${k}`} style={{ animationDelay: `${i * 70}ms` }}
          className={`text-left rounded-2xl p-4 h-32 flex flex-col justify-between border shadow-sm hover:shadow-md active:scale-[0.97] transition-[transform,box-shadow] animate-in fade-in slide-in-from-bottom-2 fill-mode-both ${primary ? "bg-[#385C4B] text-white border-[#385C4B]" : "bg-white text-[#1A1A1A] border-[#E5E5DF]"}`}>
          <div className="flex items-center justify-between">
            <span className={`text-[11px] uppercase tracking-[0.16em] font-semibold ${primary ? "text-white/80" : "text-[#6B6B61]"}`}>{label}</span>
            <Icon className={`w-5 h-5 ${primary ? "text-white/80" : tone}`} />
          </div>
          <div>
            <div className={`${HEAD} text-4xl font-semibold tabular-nums leading-none`} data-testid={`dm-tile-${k}-value`}>{value}</div>
            <div className={`text-xs mt-1.5 ${primary ? "text-white/75" : "text-[#6B6B61]"}`}>{sub}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

function AgencyList({ logins, agencies, go }) {
  const by = Object.fromEntries(agencies.map((a) => [a.group_id, a]));
  return (
    <section data-testid="dm-agency-list">
      <h3 className={`${sectionLbl} mb-2.5`}>Meri Agencies</h3>
      {logins.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#DCDCD3] bg-white/60 p-6 text-sm text-center text-[#6B6B61]" data-testid="pd-empty">
          Aapko abhi koi agency assign nahi hui. Admin se contact karo.
        </div>
      ) : (
        <div className="space-y-2.5">
          {logins.map((l) => {
            const a = by[l.group_id];
            const login = l.username && l.password ? { username: l.username, password: l.password } : null;
            return (
              <div key={l.group_id} className="flex items-center gap-2 bg-white rounded-xl border border-[#E5E5DF] pl-4 pr-2 py-3 shadow-sm" data-testid={`dm-agency-${l.group_id}`}>
                <button onClick={() => go(l.group_id)} className="flex-1 min-w-0 text-left" data-testid={`dm-agency-open-${l.group_id}`}>
                  <div className="font-semibold text-sm text-[#1A1A1A] truncate">{l.name}</div>
                  <div className="text-xs text-[#6B6B61] mt-0.5" data-testid={`dm-agency-count-${l.group_id}`}>
                    <b className="text-[#385C4B] font-mono">{a?.pending_count || 0}</b> pending
                    {a?.urgent ? <span className="text-[#D9534F]"> · {a.urgent} urgent</span> : null}
                    {a?.complaint_count ? ` · ${a.complaint_count} complaint` : ""}
                  </div>
                </button>
                <AgencyLoginButton a={{ group_id: l.group_id, name: l.name, login }} isAdmin={false} />
                <button onClick={() => go(l.group_id)} className="grid place-items-center w-9 h-9 rounded-full hover:bg-[#F4F3EC] transition-colors" aria-label="Open">
                  <ChevronRight className="w-4 h-4 text-[#6B6B61]" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
